import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { unwrap } from '@/api/envelope';
import { readDashboard, type ReadDashboardData } from '@/api/generated';

import { PRESETS, type ResolvedRange } from './dateRange';
import { toDashboardData } from './toDashboardData';

type DashboardQuery = NonNullable<ReadDashboardData['query']>;

/**
 * What the backend is asked for: a preset as its number of days, which the
 * backend ends on its own UTC today, or a custom range as its two days.
 */
export function dashboardQuery(range: ResolvedRange): DashboardQuery {
  if (range.preset === 'custom') return { from: range.from, to: range.to };
  return { range: PRESETS.find((option) => option.value === range.preset)!.days };
}

/**
 * The preset is part of the key as well as the dates: "Last 7 days" and a
 * custom range over the same seven days are two different requests, and
 * "Last 7 days" chosen on two different days is two different queries.
 *
 * So is the organization. Without it, switching organization kept the one
 * before's numbers on screen under the new one's name.
 */
export function dashboardKey(range: ResolvedRange, organizationId: string | null) {
  return [
    'dashboard',
    'summary',
    organizationId,
    range.preset,
    range.from,
    range.to,
  ] as const;
}

/**
 * The one seam between the dashboard and its numbers, for the organization
 * that is open - the one every request already names.
 *
 * A new range keeps the last numbers on screen until its own arrive, but only
 * for the same organization: another organization's numbers are never shown
 * under this one's name.
 */
export function useDashboard(range: ResolvedRange, organizationId: string | null) {
  return useQuery({
    queryKey: dashboardKey(range, organizationId),
    queryFn: async () => {
      const reply = await readDashboard({
        query: dashboardQuery(range),
        throwOnError: true,
      });
      return toDashboardData(unwrap(reply.data));
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === organizationId
        ? keepPreviousData(previous)
        : undefined,
  });
}
